import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { StorageService } from '@/services/storage';
import { useAppStore } from '@/store/app.store';

declare global {
  interface ImportMeta {
    env: {
       VITE_API_URL?: string;
    };
  }
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://crazymodifier.com/api/v1';

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Accept': 'application/json',
  },
  withCredentials: true,
});

// ─── Request interceptor — attach auth token ──────────────────────────────────

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = StorageService.getToken();
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// ─── Response interceptor ─────────────────────────────────────────────────────

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // ── Abort / cancel — let it bubble up silently, don't treat as an error ──
    if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
      return Promise.reject(error);
    }

    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // ── 401 — token expired, attempt refresh ──────────────────────────────────
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = StorageService.getRefreshToken();

        if (refreshToken) {
          const response = await axios.post(
            `${API_BASE_URL}/auth/refresh`,
            { refresh_token: refreshToken }
          );

          const { access_token, refresh_token } = response.data;

          StorageService.setToken(access_token);
          if (refresh_token) {
            StorageService.setRefreshToken(refresh_token);
          }

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
          }

          return apiClient(originalRequest);
        }
      } catch (err) {
        StorageService.clearAuth();
        window.location.href = '/auth/login';
        return Promise.reject(err);
      }
    }

    if (error.response?.status === 403) console.error('Access forbidden');
    if (error.response?.status === 404) {
       throw new Response('Not Found', { status: 404 });
    };
    if (error.response?.status === 500) console.error('Server error occurred');

    return Promise.reject(error);
  }
);

export default apiClient;